import React from "react";

import cn from "classnames";
import { css } from "@emotion/react";
import { SquareAspectRatio } from "../_shared-components/image-related/AspectRatio";
import { EqualSizeGrid } from "../_shared-components/arrangement/EqualSizeGrid";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { atMostMediaQuery, betweenMediaQuery, CSS_MQ, CSS_MQ_ONLY } from "../../styles/breakpoints";
import { SwarovskiLink } from "../_shared-components/ui-elements/SwarovskiLink";
import { TextAndImageComponentType } from "@swo/storefront";
import { getComponentCMSClassname } from "../cms/edit-overlay/component-hook-class";
import { ContentfulImage } from "../_shared-components/image-related/ContentfulImage";
import { fontColor, FONTS, fontSize } from "../../styles/fonts";
import { richTextStyles } from "../../styles/rich-text";
import { responsivePlainTextStyles } from "../../styles/text-styles";
import {
  EXPERIMENTAL_TextWithProductNameRenderer,
  useTextWithProductNameRenderer,
} from "../../domain/swarovski-optik/products/product-name/product-name-renderer";
import { SORichTextRenderer } from "../_shared-components/so-rich-text";
import { withCapitalizationClassName } from "../../domain/swarovski-optik/products/product-name/label-contains-product-name";

export function TextAndImageComponent(props: TextAndImageComponentType) {
  const imageShapeCss = props.imageShape === "Square" ? "image-square" : "image-oval";
  const imageFitCss = (() => {
    switch (props.imageFit) {
      case "fit-in": {
        return "image-fill-contain";
      }
      case "stretch-to-fill": {
        return "image-fill-cover";
      }
      default: {
        const _: never = props.imageFit;
        return _;
      }
    }
  })();

  const descriptiveHeadline = useTextWithProductNameRenderer(props.descriptiveHeadline);

  return (
    <EqualSizeGrid
      className={cn(getComponentCMSClassname(props?.sys?.id))}
      container={"--max-narrow-content-width"}
      atLeast={"md"}
      attachStyles={css`
        --margin-horizontal: 0;

        ${CSS_MQ.atLeastMd} {
          --margin-horizontal: 30px;
        }

        ${CSS_MQ.atLeastXl} {
          --margin-horizontal: 50px;
        }
      `}
    >
      <div
        className={cn(props.alignImage === "Left" ? "order-0" : "order-1", "u-relative")}
        css={css`
          &.order-0 {
            order: 0;
          }

          &.order-1 {
            order: 1;
          }

          // This wrapper is needed to enforce this styles
          // and do not distort aspect-ratio component below
          // otherwise, image may be oval and not a perfect circle ;)
          display: flex;
          justify-content: center;
          flex-direction: column;
        `}
      >
        <SquareAspectRatio
          className={cn(props.imageShape === "Oval" ? "with-oval" : "")}
          disableOnMobiles={props.imageShape === "Square"}
          attachStyles={css`
            ${CSS_MQ.atMostSm} {
              margin-left: var(--margin-horizontal);
              margin-right: var(--margin-horizontal);
            }

            &.with-oval {
              ${CSS_MQ.atMostSm} {
                margin-bottom: 12px;
              }

              ${CSS_MQ_ONLY.xs} {
                max-width: 100%;
              }

              ${CSS_MQ_ONLY.sm} {
                max-width: 350px;
              }
            }
          `}
        >
          <ContentfulImage
            assetId={props.image.sys.id}
            className={cn(imageShapeCss, imageFitCss)}
            alt={props.image.description || props.image.title}
            css={css`
              width: 100%;
              height: 100%;

              &.image-fill-contain {
                object-fit: contain;
              }
              &.image-fill-cover {
                object-fit: cover;
              }

              object-position: center;

              &.image-oval {
                border-radius: 50%;
              }
            `}
            contentfulLink={{ url: props.image.url, requestedWidth: 600 }}
            attachPictureStyles={css`
              display: flex;
            `}
          />
        </SquareAspectRatio>
      </div>

      <div
        className={cn({ "padding-right": props.alignImage === "Right", "padding-left": props.alignImage === "Left" })}
        css={css`
          &.padding-left {
            padding-left: var(--margin-horizontal);
          }

          &.padding-right {
            padding-right: var(--margin-horizontal);
          }

          padding-top: 12px;
          padding-bottom: 12px;
          ${CSS_MQ.atLeastMd} {
            padding-top: 40px;
            padding-bottom: 40px;
          }

          display: flex;
          align-items: center;
        `}
      >
        <div>
          <h2
            className={withCapitalizationClassName(descriptiveHeadline.hasAnyProductName)}
            css={[
              FONTS.SwarovskiFutura500,
              fontColor.black,
              fontSize.x2,
              css`
                margin-top: 0;
                margin-bottom: 8px;

                text-transform: uppercase;
              `,
            ]}
          >
            {descriptiveHeadline.Element}
          </h2>

          <h3
            css={[
              FONTS.MinervaModern500,
              fontSize.x4,
              css`
                margin-top: 0;
                margin-bottom: 20px;

                color: ${CSS_COLORS.Green};

                text-transform: uppercase;
                word-break: break-word;
              `,
            ]}
          >
            <EXPERIMENTAL_TextWithProductNameRenderer text={props.emoHeadline} />
          </h3>
          <hr
            css={css`
              width: 20px;
              margin-left: 0;

              border: none;
              height: 1px;
              background-color: black;
            `}
          />
          <div
            css={css`
              ${FONTS.SwarovskiFutura300};
              ${fontColor.black};

              ${responsivePlainTextStyles};
              ${richTextStyles};

              ${CSS_MQ.atLeastMd} {
                margin-bottom: 30px;
              }
            `}
          >
            <SORichTextRenderer richText={props.richText} options={"with-hyperlinks"} />
          </div>
          {props.link && (
            <SwarovskiLink
              link={props.link.data}
              isInternal={props.link.type === "Internal Link"}
              scrollToTop={true}
              styled={"underline"}
            >
              {props.link.label}
            </SwarovskiLink>
          )}
        </div>
      </div>
    </EqualSizeGrid>
  );
}
